import React, { MouseEventHandler, useCallback, useEffect, useState } from 'react'
import AccountAccessModalContent from './AccountAccessModalContent'
import ModalBase from 'LuxKit/Modal/ModalBase'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useMobileNativeAppAuth } from 'selectors/configSelectors'
import { clearAuthError, loginNativeApp } from 'actions/AuthActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { replace } from 'connected-react-router'

export interface AccountAccessModalResult {
  loggedIn?: boolean
}

interface Props {
  dismissable?: boolean
  initialMode?: App.UiAccountModalMode
  resetPasswordToken?: string
  hotelOfferDetails?: App.AccountModalOfferDetails
  offerDetails?: App.AccountModalOfferDetails
  defaultEmail?: string
  callbackPath?: string
  defaultAppleData?: App.AppleLoginData
}

function AccountAccessModal(props: Props) {
  const {
    initialMode = 'login',
    resetPasswordToken,
    hotelOfferDetails,
    offerDetails,
    dismissable = true,
    defaultEmail,
    callbackPath,
    defaultAppleData,
  } = props

  const [mode, setMode] = useState<App.UiAccountModalMode>(initialMode)
  const [secondChanceDismissed, setSecondChanceDismissed] = useState(false)
  const loading = useAppSelector(state => state.auth.processing)

  const { resolve, afterClose } = useModalElementContext<AccountAccessModalResult>()
  const useNativeAppAuth = useAppSelector(useMobileNativeAppAuth)
  const loggedIn = useAppSelector(selectLoggedIn)
  const loggedInError = useAppSelector(state => state.auth.error)
  const dispatch = useAppDispatch()
  const isSecondChanceMode = mode === 'promptAddPhone' || mode === 'promptAddPhoneOnboarding' || mode === 'joinOTP'

  useEffect(() => {
    dispatch(clearAuthError())
    return () => {
      dispatch(clearAuthError())
    }
  }, [dispatch])

  useEffect(() => {
    if (!isSecondChanceMode && secondChanceDismissed) {
      setSecondChanceDismissed(false)
    }
  }, [isSecondChanceMode, secondChanceDismissed])

  useEffect(() => {
    if (useNativeAppAuth) {
      dispatch(loginNativeApp())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useNativeAppAuth])

  useEffect(() => {
    if (useNativeAppAuth) {
      if (loggedIn) {
        resolve({ loggedIn: true })
        // afterClose **must** be called after resolve has finished resolving (it sets up state in after resolve)
        // 'resolve' resolves a promise, causing its result to be evalulated on next tick
        // To make afterClose happen after resolve, we bump it to the next tick as well
        window.setTimeout(() => afterClose(), 0)
      } else if (loggedInError) {
        resolve({ loggedIn: false })
        window.setTimeout(() => afterClose(), 0)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useNativeAppAuth, loggedIn, loggedInError])

  const afterCloseHandler = () => {
    if (callbackPath) {
      dispatch(replace(callbackPath))
    }
  }

  const handleSecondChanceClose = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    event.preventDefault()
    if (!secondChanceDismissed) {
      setSecondChanceDismissed(true)
      return
    }

    resolve({ loggedIn: true })
  }, [secondChanceDismissed, resolve])

  if (useNativeAppAuth) {
    return null
  }

  return (<ModalBase
    size="S"
    dismissible={dismissable}
    modalId="accountAccess"
    onAfterClose={afterCloseHandler}
    onClose={isSecondChanceMode ? handleSecondChanceClose : undefined}
  >
    <LoadingIndicator visible={loading} floating />
    <AccountAccessModalContent
      mode={mode}
      defaultEmail={defaultEmail}
      onModeChange={setMode}
      resetPasswordToken={resetPasswordToken}
      hotelOfferDetails={hotelOfferDetails}
      offerDetails={offerDetails}
      callbackPath={callbackPath}
      dismissable={dismissable}
      defaultAppleData={defaultAppleData}
      secondChanceDismissed={secondChanceDismissed}
      onSecondChanceDismissedChange={setSecondChanceDismissed}
    />
  </ModalBase>
  )
}

export default AccountAccessModal
